import { makeAutoObservable } from "mobx";
import { wrap } from "../util/supabase-wrapper";
import { definitions, supabase } from "./supabase/client";
import { Child } from "./child";

export class TeacherFeedback {
  teacherFeedback: Partial<definitions["TeacherFeedback"]> = {};
  class: Partial<definitions["Class"]> = {};
  level: Partial<definitions["Level"]> = {};
  // TODO: Technically we can get the module by looking at ALL the engagement panels interacted with and returning the modules
  // module: Partial<definitions["Module"]> = {};
  students: Child[] = [];
  childTeacherFeedback: Partial<definitions["ChildTeacherFeedback"]>[] = [];
  timeSlot: Partial<definitions["TimeSlot"]> = {};
  talkingPartyRoom: Partial<
    Omit<definitions["TalkingPartyRoom"], "startTime" | "endTime"> & {
      startTime: Date;
      endTime: Date;
    }
  > = {};

  constructor() {
    makeAutoObservable(this);
  }

  async init(id: number) {
    this.teacherFeedback.id = id;
    await this.pullDetails();
    return this;
  }

  async getChildTeacherFeedback(
    studentId: number
  ): Promise<Partial<definitions["ChildTeacherFeedback"]>> {
    //TODO: Throw error if child is not in the class

    const feedbackIfExists = this.childTeacherFeedback.find((feedback) => {
      return feedback.child === studentId;
    });

    if (feedbackIfExists) return feedbackIfExists;

    await supabase.from("ChildTeacherFeedback").insert({
      child: studentId,
      feedback: this.teacherFeedback.id,
    });

    await this.pullDetails();

    return this.getChildTeacherFeedback(studentId);
  }

  async rateStudentEngagement(
    studentId: number,
    engagementLevel: number | undefined
  ) {
    const childTeacherFeedback = await this.getChildTeacherFeedback(studentId);

    await supabase
      .from("ChildTeacherFeedback")
      .update({
        engagementLevel,
      })
      .match({
        id: childTeacherFeedback.id,
      });

    await this.pullDetails();
    return this;
  }

  async setStudentEngagementPlan(
    studentId: number,
    engagementIncreasePlan: string
  ) {
    const childTeacherFeedback = await this.getChildTeacherFeedback(studentId);

    await supabase
      .from("ChildTeacherFeedback")
      .update({
        engagementIncreasePlan,
      })
      .match({
        id: childTeacherFeedback.id,
      });

    await this.pullDetails();
    return this;
  }

  async setStudentFeedback(studentId: number, feedbackForChild: string) {
    const childTeacherFeedback = await this.getChildTeacherFeedback(studentId);

    await supabase
      .from("ChildTeacherFeedback")
      .update({
        feedbackForChild,
      })
      .match({
        id: childTeacherFeedback.id,
      });

    await this.pullDetails();
    return this;
  }

  async setStudentParentFeedback(studentId: number, feedbackForParent: string) {
    const childTeacherFeedback = await this.getChildTeacherFeedback(studentId);

    await supabase
      .from("ChildTeacherFeedback")
      .update({
        feedbackForParent,
      })
      .match({
        id: childTeacherFeedback.id,
      });

    await this.pullDetails();
    return this;
  }

  async setAdditionalComments(additionalComments: string) {
    await supabase
      .from("TeacherFeedback")
      .update({
        additionalComments,
      })
      .match({
        id: this.teacherFeedback.id,
      });

    await this.pullDetails();
    return this;
  }

  async setEngagementImprovementPlan(engagementImprovementPlan: string) {
    await supabase
      .from("TeacherFeedback")
      .update({
        engagementImprovementPlan,
      })
      .match({
        id: this.teacherFeedback.id,
      });

    await this.pullDetails();
    return this;
  }

  async setStudentResponseTo(
    activityType: "SONGS" | "ACTIVITIES" | "GAMES" | "COMPETITION" | "QNA",
    rating: number | undefined
  ) {
    let columnName: keyof definitions["TeacherFeedback"] = "responseToSongs";
    switch (activityType) {
      case "SONGS": {
        columnName = "responseToSongs";
        break;
      }
      case "ACTIVITIES": {
        columnName = "responseToActivities";
        break;
      }
      case "GAMES": {
        columnName = "responseToGames";
        break;
      }
      case "COMPETITION": {
        columnName = "responseToCompetition";
        break;
      }
      case "QNA": {
        columnName = "responseToQnA";
        break;
      }
    }
    await supabase
      .from("TeacherFeedback")
      .update({
        [columnName]: rating,
      })
      .match({
        id: this.teacherFeedback.id,
      })
      .throwOnError();

    await this.pullDetails();
    return this;
  }

  async setBloomsStandard(
    standard:
      | "bloomRemembering"
      | "bloomUnderstanding"
      | "bloomApplying"
      | "bloomAnalyzing"
      | "bloomEvaluating"
      | "bloomCreating",
    value: boolean
  ) {
    await supabase
      .from("TeacherFeedback")
      .update({
        [standard]: value,
      })
      .match({
        id: this.teacherFeedback.id,
      })
      .throwOnError();

    await this.pullDetails();

    return this;
  }

  async pullDetails() {
    const allDetails = wrap(
      await supabase
        .from("TeacherFeedback")
        .select(
          "*,ChildTeacherFeedback(*),TalkingPartyRoom(*,TimeSlot(*,Class(*,Level(*),ChildClass(*))))"
        )
        .match({ id: this.teacherFeedback.id })
    );

    const { ChildTeacherFeedback, TalkingPartyRoom, ...teacherFeedback } =
      allDetails[0];

    const childIds: number[] = [];

    const { TimeSlot, ...newTalkingPartyRoom } = { ...TalkingPartyRoom };

    const { Class, ...newTimeSlot } = { ...TimeSlot };

    const { ChildClass, Level, ...newClass } = { ...Class };

    (ChildClass ?? []).forEach(async (childClass: { child: number | null }) => {
      if (!childClass.child) return;
      childIds.push(childClass.child);
    });

    this.students = await Promise.all(
      childIds.map(async (childId) => {
        const newChild = new Child();
        await newChild.init(childId);

        return newChild;
      })
    );

    this.childTeacherFeedback = ChildTeacherFeedback;
    this.level = { ...this.level, ...Level };
    this.class = newClass;
    this.timeSlot = newTimeSlot;
    this.talkingPartyRoom = {
      ...newTalkingPartyRoom,
      startTime: newTalkingPartyRoom.startTime
        ? new Date(newTalkingPartyRoom.startTime)
        : undefined,
      endTime: newTalkingPartyRoom.endTime
        ? new Date(newTalkingPartyRoom.endTime)
        : undefined,
    };

    this.talkingPartyRoom.startTime = this.talkingPartyRoom.startTime
      ? new Date(this.talkingPartyRoom.startTime)
      : this.talkingPartyRoom.startTime;

    this.talkingPartyRoom.endTime = this.talkingPartyRoom.endTime
      ? new Date(this.talkingPartyRoom.endTime)
      : this.talkingPartyRoom.endTime;

    this.teacherFeedback = teacherFeedback;

    return this;
  }
}
